/**
 * Editor Visual Mockup — /reports/editor-mockup
 *
 * Pure visual scratchpad showing what the report editor would look like if
 * it adopted the app's signature visual identity (notches, inset shadow,
 * rounded-tl, brand sidebar, pill buttons). NO real state, NO real DnD,
 * NO real save logic — this is a static prototype to evaluate the look.
 *
 * Does not import or modify any of the production editor components.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useCanvasZoom, ZOOM_MIN, ZOOM_MAX } from "../../../hooks/useCanvasZoom";
import { ZoomControls } from "../../../components/reports/ZoomControls";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Eye,
  ShareNetwork,
  CalendarBlank,
  CaretDown,
  Plus,
  ArrowLineRight,
  ArrowLineLeft,
  ChartDonutIcon,
  GridNineIcon,
  Image,
  Code,
  RulerIcon,
  GaugeIcon,
  Flag,
  PaletteIcon,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { WidgetCommandPalette } from "../../../components/reports/WidgetCommandPalette";
import { IntegrationPickerPanel } from "../../../components/reports/WidgetPalette";
import { Favicon } from "../../../components/icons/Favicon";
import { Logo } from "../../../components/icons/Logo";

export const Route = createFileRoute("/_authenticated/reports/editor-mockup")({
  component: EditorMockupPage,
});

// ── Fake data ────────────────────────────────────────────────────────

const FAKE_PAGES_BY_CATEGORY: { category: string; pages: { id: string; title: string }[] }[] = [
  {
    category: "SUMMARY",
    pages: [
      { id: "p1", title: "Executive Overview" },
      { id: "p2", title: "Monthly Highlights" },
    ],
  },
  {
    category: "ACQUISITION",
    pages: [
      { id: "p3", title: "Traffic Sources" },
      { id: "p4", title: "Paid Campaigns" },
    ],
  },
  {
    category: "AUDIENCE",
    pages: [{ id: "p5", title: "Demographics" }],
  },
];

// Mirror the production WidgetCategorySidebar items 1:1
const FAKE_WIDGET_CATEGORIES: { id: string; label: string; icon: Icon }[] = [
  { id: "integrations-metrics", label: "Platform Widgets", icon: ChartDonutIcon },
  { id: "content-blocks", label: "Page Elements", icon: GridNineIcon },
  { id: "images", label: "Images", icon: Image },
  { id: "embeds", label: "Embeds", icon: Code },
  { id: "custom-metrics", label: "Custom Metrics", icon: RulerIcon },
  { id: "benchmarks", label: "Benchmarks", icon: GaugeIcon },
  { id: "goals", label: "Goals", icon: Flag },
];

const REPORT_THEME_CATEGORY = { id: "report-theme", label: "Report Theme", icon: PaletteIcon };

// ── Widget category item with portaled tooltip (collapsed only) ──
function WidgetCategoryItem({
  category,
  isActive,
  collapsed,
  onClick,
}: {
  category: { id: string; label: string; icon: Icon };
  isActive: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  const Cat = category.icon;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; right: number } | null>(null);

  const showTooltip = useCallback(() => {
    if (!collapsed || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      right: window.innerWidth - rect.left + 8,
    });
  }, [collapsed]);

  const hideTooltip = useCallback(() => setTooltipPos(null), []);

  return (
    <li className="relative cursor-pointer">
      {isActive && (
        <div className="absolute inset-0 rounded-lg bg-background border border-input shadow-xs pointer-events-none" />
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={onClick}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        aria-pressed={isActive}
        className="relative z-10 block w-full rounded-lg text-sm font-normal text-foreground hover:bg-background hover:shadow-xs border border-transparent hover:border-input cursor-pointer text-left"
      >
        <div
          className="flex items-center py-2.5"
          style={{ paddingLeft: 11, paddingRight: collapsed ? 0 : 12 }}
        >
          <Cat size={19} className="shrink-0 text-foreground" />
          <span
            className="whitespace-nowrap"
            style={{
              opacity: collapsed ? 0 : 1,
              marginLeft: collapsed ? 0 : 7,
              transition: "opacity 0.2s ease, margin-left 0.2s ease",
            }}
          >
            {category.label}
          </span>
        </div>
      </button>
      {tooltipPos &&
        createPortal(
          <div
            className="fixed z-[100] pointer-events-none -translate-y-1/2"
            style={{ top: tooltipPos.top, right: tooltipPos.right }}
          >
            <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-background border-r border-t border-input rotate-45 z-10" />
            <div className="relative bg-background text-foreground text-[13px] font-medium px-2.5 py-1 rounded-md border border-input shadow-xs whitespace-nowrap">
              {category.label}
            </div>
          </div>,
          document.body,
        )}
    </li>
  );
}

const FAKE_CLIENT_NAME = "Demo Client 1";
const FAKE_SAVE_STATUS: "saved" | "saving" | "unsaved" = "unsaved";

// ── Component ─────────────────────────────────────────────────────────

function EditorMockupPage() {
  const [activePageId, setActivePageId] = useState("p1");
  const [activeWidgetCategory, setActiveWidgetCategory] = useState<string | null>("integrations-metrics");
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(true);

  const leftWidth = leftCollapsed ? 75 : 220;
  const rightWidth = rightCollapsed ? 75 : 165;

  // ── Zoom state ────────────────────────────────────────────────
  const canvasZoom = useCanvasZoom();
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const zoomWrapperRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  // Fake "natural" paper width — same family as report rendering content width
  const CONTENT_WIDTH_PX = 980;

  const computeFitToWidth = useCallback(() => {
    const available = window.innerWidth - leftWidth - rightWidth - 64;
    canvasZoom.fitToWidth(available, CONTENT_WIDTH_PX);
  }, [canvasZoom, leftWidth, rightWidth]);

  // Cmd/Ctrl + = / - / 0 keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "=" || e.key === "+") {
        e.preventDefault();
        canvasZoom.zoomIn();
        return;
      }
      if (e.key === "-") {
        e.preventDefault();
        canvasZoom.zoomOut();
        return;
      }
      if (e.key === "0") {
        e.preventDefault();
        computeFitToWidth();
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canvasZoom, computeFitToWidth]);

  // Trackpad pinch-to-zoom — mirrors the real editor's behavior.
  // Mutates DOM synchronously for buttery 120hz feel, debounces React state sync.
  const zoomAccumRef = useRef(canvasZoom.zoomLevel);
  zoomAccumRef.current = canvasZoom.zoomLevel;
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();

      const oldZoom = zoomAccumRef.current;
      const delta = Math.max(-50, Math.min(50, -e.deltaY));
      const sensitivity = 1.5;
      const rawNewZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, oldZoom + delta * sensitivity));
      if (Math.abs(rawNewZoom - oldZoom) < 0.01) return;

      zoomAccumRef.current = rawNewZoom;
      const newScale = rawNewZoom / 100;
      const oldScale = oldZoom / 100;

      const scrollContainer = scrollContainerRef.current;
      const zoomWrapper = zoomWrapperRef.current;
      const spacer = spacerRef.current;

      if (scrollContainer && zoomWrapper && spacer) {
        const rect = scrollContainer.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        const contentX = (scrollContainer.scrollLeft + cursorX) / oldScale;
        const contentY = (scrollContainer.scrollTop + cursorY) / oldScale;

        zoomWrapper.style.transform = `scale(${newScale})`;
        const spacerWidth = zoomWrapper.offsetWidth;
        spacer.style.width = `${spacerWidth * newScale}px`;

        scrollContainer.scrollLeft = contentX * newScale - cursorX;
        scrollContainer.scrollTop = contentY * newScale - cursorY;
      }

      const zoomDisplay = document.querySelector("[data-testid='zoom-level-display']");
      if (zoomDisplay) zoomDisplay.textContent = `${Math.round(rawNewZoom)}%`;

      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => {
        canvasZoom.setZoom(zoomAccumRef.current);
      }, 100);
    };

    window.addEventListener("wheel", handler, { passive: false });
    return () => {
      window.removeEventListener("wheel", handler);
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [canvasZoom]);

  const scale = canvasZoom.zoomLevel / 100;

  return (
    <div className="fixed inset-0 z-50 flex overflow-hidden bg-background">
      {/* ── Left sidebar: Pages ───────────────────────────────────── */}
      <aside
        className="shrink-0 flex flex-col bg-background overflow-hidden"
        style={{ width: leftWidth, transition: "width 0.3s ease" }}
      >
        {/* Brand header (Favicon / Logo) — mirrors Sidebar.tsx */}
        <div className="shrink-0 px-4 pt-5 pb-1">
          <div className="relative h-12">
            <div
              className="absolute inset-y-0 left-[8px] flex items-center"
              style={{ opacity: leftCollapsed ? 1 : 0, transition: "opacity 0.2s ease" }}
            >
              <Favicon size={28} />
            </div>
            <div
              className="absolute inset-y-0 left-[-3px] flex items-center"
              style={{ opacity: leftCollapsed ? 0 : 1, transition: "opacity 0.2s ease" }}
            >
              <Logo height={58} />
            </div>
          </div>
        </div>

        {/* Pages list */}
        <nav className="flex-1 px-4 overflow-y-auto pt-4">
          {(() => {
            let globalIndex = 0;
            return FAKE_PAGES_BY_CATEGORY.map((section, idx) => (
              <div key={section.category}>
                <div className="mb-4">
                  <div
                    className="overflow-hidden"
                    style={{
                      height: leftCollapsed ? 0 : "auto",
                      opacity: leftCollapsed ? 0 : 1,
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    <div className="px-3 pb-2 pt-3 text-[11px] font-semibold tracking-wider text-foreground/70 uppercase">
                      {section.category}
                    </div>
                  </div>
                  <ul className="space-y-1">
                    {section.pages.map((page) => {
                      globalIndex += 1;
                      const pageNumber = globalIndex;
                      const isActive = page.id === activePageId;
                      return (
                        <li key={page.id} className="relative">
                          {isActive && (
                            <div className="absolute inset-0 rounded-lg bg-background border border-input shadow-xs pointer-events-none" />
                          )}
                          <button
                            type="button"
                            onClick={() => setActivePageId(page.id)}
                            onMouseEnter={(e) => {
                              if (isActive) return;
                              const badge = e.currentTarget.querySelector<HTMLElement>("[data-page-badge]");
                              if (badge) badge.style.borderColor = "hsl(214 25% 70% / 0)";
                            }}
                            onMouseLeave={(e) => {
                              if (isActive) return;
                              const badge = e.currentTarget.querySelector<HTMLElement>("[data-page-badge]");
                              if (badge) badge.style.borderColor = "hsl(214 25% 55% / 0.7)";
                            }}
                            className="relative z-10 block w-full text-left rounded-lg text-sm font-medium text-foreground hover:bg-background hover:shadow-xs border border-transparent hover:border-input cursor-pointer"
                          >
                            <div
                              className="flex items-center py-2.5"
                              style={{
                                paddingLeft: 8,
                                paddingRight: leftCollapsed ? 0 : 12,
                              }}
                            >
                              {/* Page number badge — border fades out on active + on hover, coral digit bumps up 1px */}
                              <span
                                data-page-badge
                                className="shrink-0 inline-flex items-center justify-center rounded-md font-semibold tabular-nums"
                                style={{
                                  width: 26,
                                  height: 26,
                                  fontSize: isActive ? 16 : 15,
                                  color: isActive
                                    ? "hsl(359 100% 71%)" /* coral on active */
                                    : "hsl(215 25% 45%)" /* desaturated slate on inactive */,
                                  borderWidth: "0.5px",
                                  borderStyle: "solid",
                                  borderColor: isActive
                                    ? "hsl(214 25% 70% / 0)" /* transparent (fades out) */
                                    : "hsl(214 25% 55% / 0.7)",
                                  transition:
                                    "border-color 0.25s ease, color 0.2s ease, font-size 0.2s ease",
                                }}
                              >
                                {pageNumber}
                              </span>
                              <span
                                className="whitespace-nowrap overflow-hidden text-ellipsis"
                                style={{
                                  opacity: leftCollapsed ? 0 : 1,
                                  marginLeft: leftCollapsed ? 0 : 9,
                                  transition: "opacity 0.2s ease, margin-left 0.2s ease",
                                }}
                              >
                                {page.title}
                              </span>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                {idx < FAKE_PAGES_BY_CATEGORY.length - 1 && (
                  <div
                    className="mx-2 mb-4"
                    style={{ borderBottom: "0.5px solid hsl(var(--input) / 0.5)" }}
                  />
                )}
              </div>
            ));
          })()}
        </nav>

        {/* Footer: Add page + collapse */}
        <div className="shrink-0 px-3 pb-3 space-y-2">
          {!leftCollapsed && (
            <button
              type="button"
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-2xl bg-background text-[13px] font-medium text-foreground shadow-xs px-3 py-2 cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all"
              style={{ border: "0.5px solid var(--hairline)" }}
            >
              <Plus size={14} />
              Add page
              <CaretDown size={10} className="ml-1 opacity-60" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setLeftCollapsed((v) => !v)}
            className="flex items-center w-full rounded-lg text-foreground bg-accent/50 hover:bg-accent/50 shadow-sm transition-colors cursor-pointer"
            style={{ border: "0.25px solid var(--hairline)" }}
            title={leftCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <div
              className="flex items-center py-3"
              style={{ paddingLeft: 11, paddingRight: leftCollapsed ? 0 : 12 }}
            >
              {leftCollapsed ? (
                <ArrowLineRight size={18} className="shrink-0" />
              ) : (
                <ArrowLineLeft size={18} className="shrink-0" />
              )}
              <span
                className="whitespace-nowrap text-sm font-medium"
                style={{
                  opacity: leftCollapsed ? 0 : 1,
                  marginLeft: leftCollapsed ? 0 : 7,
                  transition: "opacity 0.2s ease, margin-left 0.2s ease",
                }}
              >
                Collapse
              </span>
            </div>
          </button>
        </div>
      </aside>

      {/* ── Main editor column ───────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top thin strip with notches */}
        <div className="relative shrink-0 bg-background h-[21px] overflow-visible">
          {/* Actions notch — left: Close / Preview / Share + Save status */}
          <div className="absolute z-20 left-6 top-0 h-[48px]">
            <div className="relative h-full flex items-center gap-2 px-3 bg-background rounded-b-[20px]">
              <button
                type="button"
                className="flex items-center justify-center text-foreground hover:bg-accent/50 rounded-lg cursor-pointer transition-colors"
                style={{ width: 35, height: 35 }}
                title="Close"
                aria-label="Close editor"
              >
                <X size={17} />
              </button>
              <button
                type="button"
                className="flex items-center justify-center text-foreground hover:bg-accent/50 rounded-lg cursor-pointer transition-colors"
                style={{ width: 35, height: 35 }}
                title="Preview"
                aria-label="Preview"
              >
                <Eye size={17} />
              </button>
              <button
                type="button"
                className="flex items-center justify-center text-foreground hover:bg-accent/50 rounded-lg cursor-pointer transition-colors"
                style={{ width: 35, height: 35 }}
                title="Share"
                aria-label="Share"
              >
                <ShareNetwork size={17} />
              </button>

              {/* Save status with brand-red dot when unsaved */}
              <div className="flex items-center gap-1.5 pl-2 pr-1">
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    FAKE_SAVE_STATUS === "unsaved"
                      ? "bg-primary"
                      : FAKE_SAVE_STATUS === "saving"
                      ? "bg-muted-foreground/60 animate-pulse"
                      : "bg-muted-foreground/30"
                  }`}
                />
                <span className="text-[12px] text-muted-foreground whitespace-nowrap">
                  {FAKE_SAVE_STATUS === "unsaved"
                    ? "Unsaved changes"
                    : FAKE_SAVE_STATUS === "saving"
                    ? "Saving…"
                    : "Auto saved"}
                </span>
              </div>

              <div
                className="absolute inset-0 rounded-b-[20px] pointer-events-none"
                style={{
                  clipPath: "inset(21px -10px -10px -10px)",
                  border: "0.5px solid var(--hairline)",
                  borderTop: "none",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                }}
              />
            </div>
          </div>

          {/* Search notch — center: ⌘K trigger that opens WidgetCommandPalette */}
          <div className="absolute z-20 left-1/2 -translate-x-1/2 top-0 h-[48px]">
            <div className="relative h-full flex items-center px-4 bg-background rounded-b-[20px]" style={{ width: 240 }}>
              <button
                type="button"
                onClick={() => {
                  // Dispatch a synthetic Cmd+K — WidgetCommandPalette listens globally
                  const ev = new KeyboardEvent("keydown", {
                    key: "k",
                    metaKey: true,
                    bubbles: true,
                    cancelable: true,
                  });
                  window.dispatchEvent(ev);
                }}
                className="flex items-center gap-3 w-full text-[13.5px] text-foreground hover:text-foreground/70 transition-colors cursor-pointer"
              >
                <MagnifyingGlass size={17} className="shrink-0" />
                <span className="flex-1 text-left">Search...</span>
                <kbd className="text-xs font-medium border border-input rounded-[9px] px-1.5 py-0.5 bg-muted/40 ml-2">
                  <span className="text-sm">⌘</span> + K
                </kbd>
              </button>
              <div
                className="absolute inset-0 rounded-b-[20px] pointer-events-none"
                style={{
                  clipPath: "inset(21px -10px -10px -10px)",
                  border: "0.5px solid var(--hairline)",
                  borderTop: "none",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                }}
              />
            </div>
          </div>

          {/* Actions notch — right: Sources + Schedule */}
          <div className="absolute z-20 right-6 top-0 h-[48px]">
            <div className="relative h-full flex items-center gap-2 px-3 bg-background rounded-b-[20px]">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-2xl bg-background text-[13px] font-medium text-foreground px-3.5 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
              >
                <span className="font-semibold">3</span>
                <span className="text-muted-foreground">sources</span>
                <CaretDown size={11} className="ml-0.5 opacity-60" />
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-2xl bg-background text-[13px] font-medium text-foreground px-3.5 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
              >
                <CalendarBlank size={14} className="mr-0.5" />
                Feb 4 – Mar 6, 2026
                <CaretDown size={11} className="ml-0.5 opacity-60" />
              </button>
              <div
                className="absolute inset-0 rounded-b-[20px] pointer-events-none"
                style={{
                  clipPath: "inset(21px -10px -10px -10px)",
                  border: "0.5px solid var(--hairline)",
                  borderTop: "none",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                }}
              />
            </div>
          </div>
        </div>

        {/* Canvas main area — paper on inset-shadow background */}
        <main
          ref={scrollContainerRef as React.RefObject<HTMLElement>}
          data-testid="editor-center-canvas"
          className="flex-1 overflow-auto rounded-tl-[2rem] rounded-tr-[2rem] pt-12 pb-16"
          style={{
            backgroundColor: "hsl(210 60% 97.5%)",
            backgroundImage:
              "radial-gradient(circle, hsl(214 25% 65% / 0.42) 1px, transparent 1px)",
            backgroundSize: "20px 20px",
            boxShadow: "inset 1px 1px 3px -1px rgba(0,0,0,0.05)",
            borderLeft: "0.5px solid var(--hairline)",
            borderRight: "0.5px solid var(--hairline)",
            borderTop: "0.5px solid var(--hairline)",
          }}
        >
          {/* Spacer reserves layout space at scaled size so scrollbars track correctly */}
          <div
            ref={spacerRef}
            data-testid="canvas-editor"
            className="mx-auto"
            style={{ width: CONTENT_WIDTH_PX * scale }}
          >
            {/* Zoom wrapper — actually transformed */}
            <div
              ref={zoomWrapperRef}
              data-testid="zoom-wrapper"
              style={{
                width: CONTENT_WIDTH_PX,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }}
            >
            {/* Fake paper page */}
            <div
              className="mx-auto bg-background overflow-hidden"
              style={{
                width: "100%",
                aspectRatio: "8.5 / 11",
                borderRadius: 22,
                border: "0.5px solid var(--hairline)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              }}
            >
              <div className="p-10 h-full flex flex-col">
                {/* Fake report header */}
                <div className="flex items-center justify-between pb-6 mb-6" style={{ borderBottom: "0.5px solid hsl(214 25% 88%)" }}>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                      Performance Report
                    </div>
                    <div className="text-[21px] font-semibold text-foreground">
                      {FAKE_CLIENT_NAME}
                    </div>
                  </div>
                  <div className="text-[12px] text-muted-foreground">Feb 4 – Mar 6, 2026</div>
                </div>

                {/* Fake KPI grid */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label: "Sessions", value: "12,840", delta: "+18.4%" },
                    { label: "Conversions", value: "412", delta: "+9.1%" },
                    { label: "Revenue", value: "$24,910", delta: "+12.7%" },
                  ].map((kpi) => (
                    <div
                      key={kpi.label}
                      className="p-4 rounded-[14px]"
                      style={{ border: "0.5px solid hsl(214 25% 80%)" }}
                    >
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
                        {kpi.label}
                      </div>
                      <div className="text-[19px] font-semibold text-foreground">{kpi.value}</div>
                      <div className="text-[12px] text-primary font-medium mt-0.5">{kpi.delta}</div>
                    </div>
                  ))}
                </div>

                {/* Fake chart block */}
                <div
                  className="rounded-[14px] flex-1 flex items-center justify-center"
                  style={{ border: "0.5px solid hsl(214 25% 80%)" }}
                >
                  <div className="text-[12px] text-muted-foreground">[ chart widget ]</div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </main>
      </div>

      {/* ── Right sidebar group: integration sub-panel + widget palette ───── */}
      <div className="shrink-0 flex h-full">
        {/* Sub-panel — Platform Widgets → integration picker */}
        <AnimatePresence>
          {activeWidgetCategory === "integrations-metrics" && (
            <motion.div
              key="integration-picker"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 250, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className="shrink-0 flex flex-col bg-background overflow-hidden h-full"
              style={{ borderRight: "0.5px solid var(--hairline)" }}
            >
              <div className="flex flex-col h-full" style={{ minWidth: 250 }}>
                <IntegrationPickerPanel
                  selectedIntegration={selectedIntegration}
                  onSelect={setSelectedIntegration}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      <aside
        className="shrink-0 flex flex-col bg-background overflow-hidden"
        style={{ width: rightWidth, transition: "width 0.3s ease" }}
      >
        {/* Header to keep visual symmetry with left brand block (height-wise) */}
        <div className="shrink-0 px-4 pt-5 pb-1">
          <div className="relative h-12 flex items-center">
            <div
              className="text-[11px] font-semibold tracking-wider text-foreground/70 uppercase"
              style={{ opacity: rightCollapsed ? 0 : 1, transition: "opacity 0.2s ease" }}
            >
              Widgets
            </div>
          </div>
        </div>

        <nav className="flex-1 px-4 overflow-y-auto pt-4">
          <ul className="space-y-1.5">
            {FAKE_WIDGET_CATEGORIES.map((cat) => (
              <WidgetCategoryItem
                key={cat.id}
                category={cat}
                isActive={cat.id === activeWidgetCategory}
                collapsed={rightCollapsed}
                onClick={() => {
                  setActiveWidgetCategory((prev) => (prev === cat.id ? null : cat.id));
                  setSelectedIntegration(null);
                }}
              />
            ))}
          </ul>

          {/* Divider before Report Theme — mirrors production sidebar */}
          <div className="mx-4 my-2 border-t border-border" />

          <ul className="space-y-1.5 mt-1.5">
            <WidgetCategoryItem
              category={REPORT_THEME_CATEGORY}
              isActive={REPORT_THEME_CATEGORY.id === activeWidgetCategory}
              collapsed={rightCollapsed}
              onClick={() => {
                setActiveWidgetCategory((prev) =>
                  prev === REPORT_THEME_CATEGORY.id ? null : REPORT_THEME_CATEGORY.id,
                );
                setSelectedIntegration(null);
              }}
            />
          </ul>
        </nav>

        <div className="shrink-0 px-4 pb-3">
          <button
            type="button"
            onClick={() => setRightCollapsed((v) => !v)}
            className="flex items-center w-full rounded-lg text-foreground hover:bg-accent/50 transition-colors cursor-pointer"
            title={rightCollapsed ? "Expand widgets" : "Collapse widgets"}
          >
            <div
              className="flex items-center py-3"
              style={{ paddingLeft: 12, paddingRight: rightCollapsed ? 0 : 12 }}
            >
              {rightCollapsed ? (
                <ArrowLineLeft size={18} className="shrink-0" />
              ) : (
                <ArrowLineRight size={18} className="shrink-0" />
              )}
              <span
                className="whitespace-nowrap text-sm font-medium"
                style={{
                  opacity: rightCollapsed ? 0 : 1,
                  marginLeft: rightCollapsed ? 0 : 8,
                  transition: "opacity 0.2s ease, margin-left 0.2s ease",
                }}
              >
                Collapse
              </span>
            </div>
          </button>
        </div>
      </aside>
      </div>

      {/* Production Cmd+K command palette — fully functional, no-op add */}
      <WidgetCommandPalette onAddWidget={() => {}} />

      {/* Floating zoom controls — bottom-left of canvas area */}
      <ZoomControls
        zoomLevel={canvasZoom.displayZoom}
        onZoomIn={canvasZoom.zoomIn}
        onZoomOut={canvasZoom.zoomOut}
        onFitToWidth={computeFitToWidth}
        minZoom={ZOOM_MIN}
        maxZoom={ZOOM_MAX}
        canvasLeftOffset={leftWidth}
        canvasRightOffset={rightWidth}
      />
    </div>
  );
}
